import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ImageViewer } from '../components/ui/image-viewer';
import {
  Plus, Search, User, Phone, MapPin, Wifi, Calendar,
  UserCheck, Play, CheckCircle, XCircle, Eye, Edit2, Trash2,
  Clock, Building2, Cable, FileText, MessageSquare, Image as ImageIcon, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to extract image URL from different formats
const getImageUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object') {
    return img.thumbnail || img.full || '';
  }
  return '';
};

// Helper function to open base64 image in new tab
const openImageInNewTab = async (img, connectionId = null, imgIndex = 0) => {
  let imageUrl = getImageUrl(img);
  if (!imageUrl) return;
  
  // Try to get full resolution image from API if connectionId is provided
  if (connectionId) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/connections/${connectionId}/full-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.images && response.data.images.length > imgIndex) {
        imageUrl = response.data.images[imgIndex];
      }
    } catch (error) {
      console.error('Error fetching full image:', error);
    }
  }
  
  // If it's a base64 image, convert to blob and open
  if (imageUrl.startsWith('data:')) {
    try {
      const byteString = atob(imageUrl.split(',')[1]);
      const mimeType = imageUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error opening image:', error);
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`<html><head><title>Şəkil</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000;"><img src="${imageUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;"/></body></html>`);
        newWindow.document.close();
      }
    }
  } else {
    window.open(imageUrl, '_blank');
  }
};

// Relative time function
const getRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'İndicə yaradılıb';
  if (diffMins < 60) return `${diffMins} dəqiqə əvvəl yaradılıb`;
  if (diffHours < 24) return `${diffHours} saat əvvəl yaradılıb`;
  if (diffDays === 1) return 'Dünən yaradılıb';
  if (diffDays < 7) return `${diffDays} gün əvvəl yaradılıb`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} həftə əvvəl yaradılıb`;
  return date.toLocaleDateString('az-AZ') + ' tarixində yaradılıb';
};

// Check if scheduled date has passed
const isDatePassed = (dateString) => {
  if (!dateString) return false;
  const scheduledDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduledDate < today;
};

const STATUS_CONFIG = {
  new: { label: 'Yeni', color: 'bg-yellow-100 text-yellow-700', icon: Clock, border: 'border-l-yellow-500' },
  checking: { label: 'Yoxlanılır', color: 'bg-blue-100 text-blue-700', icon: Search, border: 'border-l-blue-500' },
  scheduled: { label: 'Planlanıb', color: 'bg-purple-100 text-purple-700', icon: Calendar, border: 'border-l-purple-500' },
  in_progress: { label: 'İcrada', color: 'bg-orange-100 text-orange-700', icon: Play, border: 'border-l-orange-500' },
  completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle, border: 'border-l-green-500' },
  rejected: { label: 'İmtina edildi', color: 'bg-amber-100 text-amber-700', icon: XCircle, border: 'border-l-amber-500' },
  not_completed: { label: 'Qoşulmadı', color: 'bg-rose-100 text-rose-700', icon: XCircle, border: 'border-l-rose-500' },
  cancelled: { label: 'Ləğv edildi', color: 'bg-red-100 text-red-700', icon: XCircle, border: 'border-l-red-500' },
};

const REJECTION_REASONS = [
  { value: 'no_space', label: 'Qutuda Yer Yoxdur' },
  { value: 'no_network', label: 'Şəbəkə Mövcud Deyil' },
  { value: 'wrong_technician', label: 'Texnik Düz Təyin Edilməyib' },
  { value: 'customer_refused', label: 'Müştəri İmtina Etdi' },
];

const SERVICE_TYPES = [
  { value: 'internet', label: 'İnternet' },
  { value: 'iptv', label: 'IPTV' },
  { value: 'catv', label: 'CaTV' },
  { value: 'telefon', label: 'Telefon' },
];

export default function ConnectionsPanel({ user }) {
  const [connections, setConnections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceTypeError, setServiceTypeError] = useState(false);
  const [groupError, setGroupError] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showRejectReviewDialog, setShowRejectReviewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [operatorNote, setOperatorNote] = useState('');
  const [editConnection, setEditConnection] = useState(null);
  
  // Comment and Image states
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [imageViewerImages, setImageViewerImages] = useState([]);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);
  
  // Safe dialog close handlers
  const closeDetailDialog = (open) => {
    if (!open) {
      setShowDetailDialog(false);
      setNewComment('');
      setTimeout(() => setSelectedConnection(null), 100);
    }
  };
  
  // Add comment handler
  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedConnection) return;
    
    setSendingComment(true);
    try {
      const response = await axios.post(
        `${API}/connections/${selectedConnection.id}/comments`,
        { text: newComment.trim() },
        { headers }
      );
      
      // Update selected connection with new comment
      const updatedComments = [...(selectedConnection.comments || []), response.data.comment];
      setSelectedConnection({ ...selectedConnection, comments: updatedComments });
      setNewComment('');
      toast.success('Şərh əlavə edildi');
      
      // Refresh connections list
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şərh əlavə edilə bilmədi');
    } finally {
      setSendingComment(false);
    }
  };
  
  // Open image viewer with full resolution images
  const openImageViewer = async (connectionId, images, startIndex = 0) => {
    // First show with current images (thumbnails)
    setImageViewerImages(images);
    setImageViewerIndex(startIndex);
    setShowImageViewer(true);
    
    // Then try to fetch full resolution images
    if (connectionId) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/connections/${connectionId}/full-images`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.images && response.data.images.length > 0) {
          setImageViewerImages(response.data.images);
        }
      } catch (error) {
        console.error('Error fetching full images:', error);
        // Keep showing thumbnails if fetch fails
      }
    }
  };
  
  // Open detail dialog with full connection data (including images)
  const openDetailDialog = async (conn) => {
    try {
      // First set basic info to show dialog immediately
      setSelectedConnection(conn);
      setShowDetailDialog(true);
      
      // Then fetch full details including images
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/connections/${conn.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update with full data including completion_images
      setSelectedConnection(response.data);
    } catch (error) {
      console.error('Error fetching connection details:', error);
      // Keep showing with basic data if fetch fails
    }
  };
  
  const closeAssignDialog = (open) => {
    if (!open) {
      setShowAssignDialog(false);
      setTimeout(() => setSelectedConnection(null), 100);
    }
  };
  
  const closeRejectReviewDialog = (open) => {
    if (!open) {
      setShowRejectReviewDialog(false);
      setOperatorNote('');
      setTimeout(() => setSelectedConnection(null), 100);
    }
  };
  
  const closeEditDialog = (open) => {
    if (!open) {
      setShowEditDialog(false);
      setTimeout(() => setEditConnection(null), 100);
    }
  };
  
  // Form data
  const [tariffs, setTariffs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [newConnection, setNewConnection] = useState({
    customer_name: '',
    customer_phone: '',
    customer_phone_2: '',
    customer_email: '',
    customer_id_number: '',
    customer_fin: '',
    reg_city: '',
    reg_district: '',
    reg_address: '',
    city: '',
    district: '',
    street: '',
    building: '',
    apartment: '',
    floor: '',
    entrance: '',
    latitude: null,
    longitude: null,
    services: {
      internet: false,
      iptv: false,
      catv: false,
      telefon: false
    },
    tariff_id: '',
    iptv_count: 0,
    ip_phone_count: 0,
    group_id: '',
    operator_notes: ''
  });
  const [assignData, setAssignData] = useState({
    technician_id: '',
    scheduled_date: '',
    scheduled_time: ''
  });
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // Initial load - fetch all data once
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Listen for global "open-connection" event (from NotificationBell click)
  useEffect(() => {
    const handler = async (e) => {
      const id = e?.detail?.id;
      if (!id) return;
      try {
        const res = await axios.get(`${API}/connections/${id}`, { headers });
        if (res.data) {
          setSelectedConnection(res.data);
          setShowDetailDialog(true);
        }
      } catch (err) {
        console.error('open-connection failed:', err);
      }
    };
    window.addEventListener('open-connection', handler);
    return () => window.removeEventListener('open-connection', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Filter connections when tab changes
  useEffect(() => {
    if (connections.length > 0 || !loading) {
      // Just filter existing data, don't re-fetch
    }
  }, [activeTab]);
  
  const checkOverdueConnections = async () => {
    try {
      await axios.get(`${API}/connections/overdue/check`, { headers });
    } catch (error) {
      console.error('Error checking overdue connections:', error);
    }
  };
  
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [connRes, statsRes, tariffsRes, groupsRes, techRes] = await Promise.all([
        axios.get(`${API}/connections`, { headers }),
        axios.get(`${API}/connections/stats`, { headers }),
        axios.get(`${API}/contract/tariffs?active_only=true`),
        axios.get(`${API}/groups`, { headers }),
        axios.get(`${API}/admin/technicians`, { headers })
      ]);
      setConnections(connRes.data.connections || connRes.data);
      setStats(statsRes.data);
      setTariffs(tariffsRes.data);
      setGroups(groupsRes.data);
      setTechnicians(techRes.data);
      
      // Check overdue in background
      checkOverdueConnections();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [connRes, statsRes] = await Promise.all([
        axios.get(`${API}/connections`, { headers }),
        axios.get(`${API}/connections/stats`, { headers })
      ]);
      setConnections(connRes.data.connections || connRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateConnection = async (e) => {
    e.preventDefault();
    try {
      // Convert services object to service_type string
      const selectedServices = Object.entries(newConnection.services)
        .filter(([key, value]) => value)
        .map(([key]) => key);
      
      // Validate all required fields at once
      let hasError = false;
      
      // Validate at least one service type is selected
      if (selectedServices.length === 0) {
        setServiceTypeError(true);
        hasError = true;
      } else {
        setServiceTypeError(false);
      }
      
      // Validate group/region is selected
      if (!newConnection.group_id) {
        setGroupError(true);
        hasError = true;
      } else {
        setGroupError(false);
      }
      
      if (hasError) {
        return;
      }
      
      const serviceType = selectedServices.join('+');
      
      const connectionData = {
        ...newConnection,
        service_type: serviceType
      };
      delete connectionData.services;
      
      const res = await axios.post(`${API}/connections`, connectionData, { headers });
      toast.success(`Qoşulma müraciəti yaradıldı: ${res.data.connection_number}`);
      setShowCreateDialog(false);
      setNewConnection({
        customer_name: '',
        customer_phone: '',
        customer_phone_2: '',
        customer_email: '',
        customer_id_number: '',
        customer_fin: '',
        reg_city: '',
        reg_district: '',
        reg_address: '',
        city: '',
        district: '',
        street: '',
        building: '',
        apartment: '',
        floor: '',
        entrance: '',
        latitude: null,
        longitude: null,
        services: {
          internet: false,
          iptv: false,
          catv: false,
          telefon: false
        },
        tariff_id: '',
        iptv_count: 0,
        ip_phone_count: 0,
        group_id: '',
        operator_notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Qoşulma yaratmaq mümkün olmadı');
    }
  };
  
  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/connections/${selectedConnection.id}/assign`, assignData, { headers });
      toast.success('Texnik təyin edildi');
      setShowAssignDialog(false);
      setAssignData({ technician_id: '', scheduled_date: '', scheduled_time: '' });
      fetchData();
    } catch (error) {
      toast.error('Texnik təyin etmək mümkün olmadı');
    }
  };
  
  const handleStatusChange = async (connectionId, newStatus) => {
    try {
      await axios.put(`${API}/connections/${connectionId}/status/${newStatus}`, {}, { headers });
      toast.success('Status dəyişdirildi');
      fetchData();
    } catch (error) {
      toast.error('Status dəyişmək mümkün olmadı');
    }
  };
  
  const handleCancel = async (connectionId) => {
    const reason = prompt('Ləğv səbəbini daxil edin:');
    if (reason === null) return;
    try {
      await axios.post(`${API}/connections/${connectionId}/cancel?reason=${encodeURIComponent(reason)}`, {}, { headers });
      toast.success('Qoşulma ləğv edildi');
      fetchData();
    } catch (error) {
      toast.error('Ləğv etmək mümkün olmadı');
    }
  };
  
  const filteredConnections = connections.filter(conn => {
    // Status filter (by active tab)
    if (activeTab !== 'all' && conn.status !== activeTab) {
      return false;
    }
    // Search filter
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (
        conn.customer_name?.toLowerCase().includes(s) ||
        conn.subscriber_name?.toLowerCase().includes(s) ||
        conn.customer_phone?.includes(searchTerm) ||
        conn.connection_number?.toLowerCase().includes(s) ||
        conn.street?.toLowerCase().includes(s) ||
        conn.comments?.some(c => c.text?.toLowerCase().includes(s))
      );
    }
    return true;
  });
  
  const tabs = [
    { id: 'new', label: 'Yeni', count: stats?.new || 0 },
    { id: 'checking', label: 'Yoxlanılır', count: stats?.checking || 0 },
    { id: 'scheduled', label: 'Planlanıb', count: stats?.scheduled || 0 },
    { id: 'in_progress', label: 'İcrada', count: stats?.in_progress || 0 },
    { id: 'rejected', label: 'İmtina edildi', count: stats?.rejected || 0 },
    { id: 'completed', label: 'Tamamlandı', count: stats?.completed || 0 },
    { id: 'not_completed', label: 'Qoşulmadı', count: stats?.not_completed || 0 },
    { id: 'all', label: 'Hamısı', count: stats?.total || 0 },
  ];
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-zinc-800">Yeni Qoşulmalar</h2>
          <p className="text-zinc-500">Yeni abunəçi qoşulma müraciətləri</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4 mr-2" />
          Yeni Qoşulma
        </Button>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-700">{stats.new}</div>
              <div className="text-sm text-yellow-600">Yeni</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{stats.checking}</div>
              <div className="text-sm text-blue-600">Yoxlanılır</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">{stats.scheduled}</div>
              <div className="text-sm text-purple-600">Planlanıb</div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-700">{stats.in_progress}</div>
              <div className="text-sm text-orange-600">İcrada</div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">{stats.rejected || 0}</div>
              <div className="text-sm text-amber-600">İmtina edildi</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
              <div className="text-sm text-green-600">Tamamlandı</div>
            </CardContent>
          </Card>
          <Card className="bg-rose-50 border-rose-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-rose-700">{stats.not_completed || 0}</div>
              <div className="text-sm text-rose-600">Qoşulmadı</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-50 border-zinc-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-zinc-700">{stats.total}</div>
              <div className="text-sm text-zinc-600">Cəmi</div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'bg-green-600' : ''}
          >
            {tab.label} ({tab.count})
          </Button>
        ))}
      </div>
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          placeholder="Axtar (ad, telefon, nömrə, ünvan)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {/* Connection Cards */}
      {loading ? (
        <div className="text-center py-8 text-zinc-500">Yüklənir...</div>
      ) : filteredConnections.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">Qoşulma tapılmadı</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConnections.map(conn => {
            const statusConfig = STATUS_CONFIG[conn.status] || STATUS_CONFIG.new;
            const StatusIcon = statusConfig.icon;
            // D2D source-dan gələn connection-lar üçün subscriber_name istifadə et
            const displayName = conn.customer_name || conn.subscriber_name || 'Ad qeyd edilməyib';
            const displayPhone = conn.customer_phone || conn.phone || '-';
            const displayAddress = conn.street && conn.building 
              ? `${conn.street} ${conn.building}/${conn.apartment || ''}`
              : conn.address || 'Ünvan qeyd edilməyib';
            
            return (
              <Card key={conn.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{displayName}</CardTitle>
                      <p className="text-sm text-zinc-500">{conn.connection_number}</p>
                      {conn.source === 'd2d' && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">D2D</span>
                      )}
                      {conn.source === 'online' && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Onlayn müraciət</span>
                      )}
                    </div>
                    <Badge className={statusConfig.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-zinc-400" />
                    <span>{displayPhone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-zinc-400" />
                    <span className="truncate">{displayAddress}</span>
                  </div>
                  {conn.assigned_technician_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <UserCheck className="h-4 w-4 text-zinc-400" />
                      <span>{conn.assigned_technician_name}</span>
                      {conn.scheduled_date && (
                        <span className={`${isDatePassed(conn.scheduled_date) ? 'text-red-600 font-semibold' : 'text-zinc-500'}`}>
                          • {conn.scheduled_date} {isDatePassed(conn.scheduled_date) && '⚠️'}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <Clock className="h-4 w-4" />
                    <span>{new Date(conn.created_at).toLocaleString('az-AZ', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })} qeydə alınıb</span>
                  </div>
                  {conn.notes && (
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <FileText className="h-4 w-4 text-zinc-400" />
                      <span className="truncate">{conn.notes.length > 50 ? conn.notes.substring(0, 50) + '...' : conn.notes}</span>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => openDetailDialog(conn)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    
                    {conn.status === 'new' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleStatusChange(conn.id, 'checking')}>
                          Yoxla
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleCancel(conn.id)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    
                    {conn.status === 'checking' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => {
                          // D2D source-dan gələn connection üçün sahələri map et
                          const editData = {
                            ...conn,
                            customer_name: conn.customer_name || conn.subscriber_name || '',
                            customer_phone: conn.customer_phone || conn.phone || '',
                            street: conn.street || conn.address || '',
                            latitude: conn.latitude || conn.location?.lat || null,
                            longitude: conn.longitude || conn.location?.lng || null,
                          };
                          setEditConnection(editData);
                          setShowEditDialog(true);
                        }}>
                          <Edit2 className="h-4 w-4 mr-1" />
                          Dəyiş
                        </Button>
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setSelectedConnection(conn); setShowAssignDialog(true); }}>
                          <UserCheck className="h-4 w-4 mr-1" />
                          Texnik Təyin Et
                        </Button>
                      </>
                    )}
                    
                    {conn.status === 'scheduled' && (
                      <span className="text-sm text-purple-600">Texnikə təyin edilib</span>
                    )}
                    
                    {conn.status === 'rejected' && (
                      <>
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => { setSelectedConnection(conn); setShowAssignDialog(true); }}>
                          <UserCheck className="h-4 w-4 mr-1" />
                          Yeni Texnik
                        </Button>
                        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setSelectedConnection(conn); setOperatorNote(''); setShowRejectReviewDialog(true); }}>
                          <XCircle className="h-4 w-4 mr-1" />
                          Qoşulmadı
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Rejection info display */}
                  {conn.status === 'rejected' && conn.rejection_reason && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                      <p className="font-medium text-amber-800">İmtina səbəbi: {REJECTION_REASONS.find(r => r.value === conn.rejection_reason)?.label || conn.rejection_reason}</p>
                      {conn.rejection_note && <p className="text-amber-700 mt-1">{conn.rejection_note}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Create Connection Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Qoşulma Müraciəti</DialogTitle>
            <DialogDescription className="text-zinc-500">Yeni abunəçi qoşulması üçün məlumatları daxil edin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateConnection} className="space-y-4">
            {/* Customer Info */}
            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Müştəri Məlumatları</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Soyad, Ad, Ata adı *</Label>
                  <Input value={newConnection.customer_name} onChange={(e) => setNewConnection({...newConnection, customer_name: e.target.value})} required className="border-gray-300" />
                </div>
                <div>
                  <Label>Telefon *</Label>
                  <Input value={newConnection.customer_phone} onChange={(e) => setNewConnection({...newConnection, customer_phone: e.target.value})} required className="border-gray-300" />
                </div>
                <div>
                  <Label>Əlavə telefon</Label>
                  <Input value={newConnection.customer_phone_2} onChange={(e) => setNewConnection({...newConnection, customer_phone_2: e.target.value})} className="border-gray-300" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={newConnection.customer_email} onChange={(e) => setNewConnection({...newConnection, customer_email: e.target.value})} className="border-gray-300" />
                </div>
              </div>
            </div>
            
            {/* Address */}
            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4" /> Qoşulma Ünvanı</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Şəhər/Rayon</Label>
                  <Input value={newConnection.city} onChange={(e) => setNewConnection({...newConnection, city: e.target.value})} className="border-gray-300" />
                </div>
                <div>
                  <Label>Qəsəbə/Kənd</Label>
                  <Input value={newConnection.district} onChange={(e) => setNewConnection({...newConnection, district: e.target.value})} className="border-gray-300" />
                </div>
                <div>
                  <Label>Küçə</Label>
                  <Input value={newConnection.street} onChange={(e) => setNewConnection({...newConnection, street: e.target.value})} className="border-gray-300" />
                </div>
                <div>
                  <Label>Bina/Ev</Label>
                  <Input value={newConnection.building} onChange={(e) => setNewConnection({...newConnection, building: e.target.value})} className="border-gray-300" />
                </div>
                <div>
                  <Label>Mənzil</Label>
                  <Input value={newConnection.apartment} onChange={(e) => setNewConnection({...newConnection, apartment: e.target.value})} className="border-gray-300" />
                </div>
                <div>
                  <Label>Mərtəbə</Label>
                  <Input value={newConnection.floor} onChange={(e) => setNewConnection({...newConnection, floor: e.target.value})} className="border-gray-300" />
                </div>
              </div>
              
              {/* Google Maps URL or Coordinates */}
              <div className="pt-2 border-t">
                <Label>Google Maps URL və ya Koordinatlar</Label>
                <Input
                  placeholder="https://maps.google.com/?q=40.4093,49.8671 və ya 40.4093, 49.8671"
                  className="mt-1.5 placeholder:text-gray-400 border-gray-300"
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    let lat = null, lng = null;
                    
                    // Pattern 1: https://maps.google.com/?q=40.4093,49.8671
                    const qMatch = value.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (qMatch) {
                      lat = parseFloat(qMatch[1]);
                      lng = parseFloat(qMatch[2]);
                    }
                    
                    // Pattern 2: https://www.google.com/maps/@40.4093,49.8671,15z
                    const atMatch = value.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (!lat && atMatch) {
                      lat = parseFloat(atMatch[1]);
                      lng = parseFloat(atMatch[2]);
                    }
                    
                    // Pattern 3: Direct coordinates "40.4093, 49.8671"
                    const coordMatch = value.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/);
                    if (!lat && coordMatch) {
                      lat = parseFloat(coordMatch[1]);
                      lng = parseFloat(coordMatch[2]);
                    }
                    
                    // Pattern 4: https://maps.google.com/maps?ll=40.4093,49.8671
                    const llMatch = value.match(/ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (!lat && llMatch) {
                      lat = parseFloat(llMatch[1]);
                      lng = parseFloat(llMatch[2]);
                    }
                    
                    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                      setNewConnection({...newConnection, latitude: lat, longitude: lng});
                    }
                  }}
                />
                <p className="text-xs text-zinc-500 mt-1">Google Maps linkini yapışdırın, koordinatlar avtomatik çıxarılacaq</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Enlik (Latitude)</Label>
                  <Input 
                    type="number" 
                    step="any"
                    value={newConnection.latitude || ''} 
                    onChange={(e) => setNewConnection({...newConnection, latitude: parseFloat(e.target.value)})}
                    className="border-gray-300"
                  />
                </div>
                <div>
                  <Label>Uzunluq (Longitude)</Label>
                  <Input 
                    type="number" 
                    step="any"
                    value={newConnection.longitude || ''} 
                    onChange={(e) => setNewConnection({...newConnection, longitude: parseFloat(e.target.value)})}
                    className="border-gray-300"
                  />
                </div>
              </div>
            </div>
            
            {/* Service */}
            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><Wifi className="h-4 w-4" /> Xidmət</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Xidmət növü *</Label>
                    {serviceTypeError && (
                      <span className="text-red-500 text-sm">Ən az bir xidmət növü seçilməlidir</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {SERVICE_TYPES.map(s => (
                      <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newConnection.services[s.value]}
                          onChange={(e) => {
                            setNewConnection({
                              ...newConnection,
                              services: {...newConnection.services, [s.value]: e.target.checked}
                            });
                            setServiceTypeError(false);
                          }}
                          className={`w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${serviceTypeError ? 'border-red-500' : ''}`}
                        />
                        <span className="text-sm">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className={groupError ? 'text-red-500' : ''}>Qrup/Region * {groupError && <span className="text-red-500 text-sm ml-1">(Mütləq seçilməlidir)</span>}</Label>
                  <Select value={newConnection.group_id} onValueChange={(v) => { setNewConnection({...newConnection, group_id: v}); setGroupError(false); }} required>
                    <SelectTrigger className={groupError ? 'border-red-500' : 'border-gray-300'}><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>
                      {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Qeydlər</Label>
              <Textarea value={newConnection.operator_notes} onChange={(e) => setNewConnection({...newConnection, operator_notes: e.target.value})} rows={2} className="border-gray-300" />
            </div>
            
            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">Yarat</Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Assign Technician Dialog */}
      {showAssignDialog && selectedConnection && (
        <Dialog open={showAssignDialog} onOpenChange={closeAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Texnik Təyin Et</DialogTitle>
              <DialogDescription>{selectedConnection.customer_name} - {selectedConnection.connection_number}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAssign} className="space-y-4">
              <div>
                <Label>Texnik *</Label>
                <Select value={assignData.technician_id} onValueChange={(v) => setAssignData({...assignData, technician_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    {technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name || t.username}</SelectItem>)}
                  </SelectContent>
              </Select>
              </div>
              <div>
                <Label>Tarix *</Label>
                <Input 
                  type="date" 
                  value={assignData.scheduled_date} 
                  onChange={(e) => setAssignData({...assignData, scheduled_date: e.target.value})} 
                  min={new Date().toISOString().split('T')[0]}
                  required 
                />
              </div>
              <div>
                <Label>Saat</Label>
                <Input type="time" value={assignData.scheduled_time} onChange={(e) => setAssignData({...assignData, scheduled_time: e.target.value})} />
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">Təyin Et</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Detail Dialog */}
      {showDetailDialog && selectedConnection && (
        <Dialog open={showDetailDialog} onOpenChange={closeDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <DialogTitle>Qoşulma Detalları</DialogTitle>
                <Badge className={`${STATUS_CONFIG[selectedConnection.status]?.color} text-sm px-3 py-1`}>
                  {STATUS_CONFIG[selectedConnection.status]?.label}
                </Badge>
              </div>
              <DialogDescription>{selectedConnection.connection_number}</DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              {/* Müştəri Məlumatları */}
              <div className="border rounded-lg p-4 bg-zinc-50">
                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" /> Müştəri Məlumatları
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-500 text-xs">Soyad, Ad, Ata adı</Label>
                    <p className="font-medium">{selectedConnection.customer_name}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Əsas Telefon</Label>
                    <p className="font-medium">{selectedConnection.customer_phone}</p>
                  </div>
                  {selectedConnection.customer_phone_2 && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Əlavə Telefon</Label>
                      <p className="font-medium">{selectedConnection.customer_phone_2}</p>
                    </div>
                  )}
                  {selectedConnection.customer_email && (
                    <div>
                      <Label className="text-zinc-500 text-xs">E-poçt</Label>
                      <p className="font-medium">{selectedConnection.customer_email}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Ünvan Məlumatları */}
              <div className="border rounded-lg p-4 bg-zinc-50">
                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Ünvan Məlumatları
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-500 text-xs">Qrup</Label>
                    <p className="font-medium">{selectedConnection.group_name || groups.find(g => g.id === selectedConnection.group_id)?.name || '-'}</p>
                  </div>
                  {selectedConnection.city && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Şəhər/Rayon</Label>
                      <p className="font-medium">{selectedConnection.city}</p>
                    </div>
                  )}
                  {selectedConnection.district && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Qəsəbə/Kənd</Label>
                      <p className="font-medium">{selectedConnection.district}</p>
                    </div>
                  )}
                  {selectedConnection.street && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Küçə</Label>
                      <p className="font-medium">{selectedConnection.street}</p>
                    </div>
                  )}
                  {selectedConnection.building && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Bina</Label>
                      <p className="font-medium">{selectedConnection.building}</p>
                    </div>
                  )}
                  {selectedConnection.apartment && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Mənzil</Label>
                      <p className="font-medium">{selectedConnection.apartment}</p>
                    </div>
                  )}
                  {selectedConnection.google_maps_url && (
                    <div className="col-span-2">
                      <Label className="text-zinc-500 text-xs">Google Maps</Label>
                      <a href={selectedConnection.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm break-all">
                        {selectedConnection.google_maps_url}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Xidmət Növləri */}
              <div className="border rounded-lg p-4 bg-zinc-50">
                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                  <Wifi className="h-4 w-4" /> Xidmət Növü
                </h3>
                <div className="flex items-center gap-4">
                  {(() => {
                    const serviceTypesArray = selectedConnection.service_types || 
                      (selectedConnection.service_type ? selectedConnection.service_type.split('+') : []);
                    
                    return SERVICE_TYPES.filter(service => 
                      serviceTypesArray.includes(service.value)
                    ).map(service => (
                      <div key={service.value} className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded border-2 flex items-center justify-center bg-green-500 border-green-500">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-medium text-green-700">{service.label}</span>
                      </div>
                    ));
                  })()}
                  {!selectedConnection.service_types?.length && !selectedConnection.service_type && (
                    <span className="text-sm text-gray-500">-</span>
                  )}
                </div>
              </div>

              {/* Texnik Məlumatları */}
              <div className="border rounded-lg p-4 bg-blue-50">
                <h3 className="font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                  <UserCheck className="h-4 w-4" /> Texnik Məlumatları
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-500 text-xs">Təyin edilmiş texnik</Label>
                    <p className="font-medium">{selectedConnection.assigned_technician_name || '-'}</p>
                  </div>
                  {selectedConnection.scheduled_date && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Planlanmış tarix</Label>
                      <p className={`font-medium ${isDatePassed(selectedConnection.scheduled_date) ? 'text-red-600' : ''}`}>
                        {selectedConnection.scheduled_date}
                        {isDatePassed(selectedConnection.scheduled_date) && (
                          <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Gecikib!</span>
                        )}
                      </p>
                    </div>
                  )}
                  {selectedConnection.ont_serial && (
                    <div>
                      <Label className="text-zinc-500 text-xs">ONU Serial</Label>
                      <p className="font-medium">{selectedConnection.ont_serial}</p>
                    </div>
                  )}
                  {selectedConnection.contract_number && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Müqavilə №</Label>
                      <p className="font-medium">{selectedConnection.contract_number}</p>
                    </div>
                  )}
                </div>
                
                {/* Texnikin Qeydləri */}
                {selectedConnection.technician_notes && (
                  <div className="mt-4 p-3 bg-white rounded border">
                    <div className="flex justify-between items-start">
                      <Label className="text-zinc-500 text-xs">Texnikin Qeydləri</Label>
                      <span className="text-xs text-zinc-400">{new Date(selectedConnection.updated_at || selectedConnection.created_at).toLocaleString('az-AZ')}</span>
                    </div>
                    <p className="mt-1">{selectedConnection.technician_notes}</p>
                  </div>
                )}
                
                {/* Texnikin Şəkili */}
                {selectedConnection.completion_photo && (
                  <div className="mt-4">
                    <Label className="text-zinc-500 text-xs">Tamamlama Şəkili</Label>
                    <div className="mt-2">
                      <img 
                        src={selectedConnection.completion_photo} 
                        alt="Tamamlama şəkili" 
                        className="w-24 h-24 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => window.open(selectedConnection.completion_photo, '_blank')}
                      />
                      <p className="text-xs text-zinc-500 mt-1">Böyütmək üçün klikləyin</p>
                    </div>
                  </div>
                )}
              </div>

              {/* İmtina Məlumatları (əgər varsa) */}
              {(selectedConnection.rejection_reason || selectedConnection.rejection_notes) && (
                <div className="border rounded-lg p-4 bg-red-50">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-red-700 flex items-center gap-2">
                      <XCircle className="h-4 w-4" /> İmtina Məlumatları
                    </h3>
                    <span className="text-xs text-zinc-500">{new Date(selectedConnection.rejected_at || selectedConnection.updated_at || selectedConnection.created_at).toLocaleString('az-AZ')}</span>
                  </div>
                  {selectedConnection.rejection_reason && (
                    <div className="mb-2">
                      <Label className="text-zinc-500 text-xs">Səbəb</Label>
                      <p className="font-medium">{REJECTION_REASONS.find(r => r.value === selectedConnection.rejection_reason)?.label || selectedConnection.rejection_reason}</p>
                    </div>
                  )}
                  {selectedConnection.rejection_notes && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Əlavə Qeyd</Label>
                      <p>{selectedConnection.rejection_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Operator Qeydləri */}
              {(selectedConnection.operator_notes || selectedConnection.notes) && (
                <div className="border rounded-lg p-4 bg-amber-50">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Qeydlər
                    </h3>
                    <span className="text-xs text-zinc-500">{new Date(selectedConnection.updated_at || selectedConnection.created_at).toLocaleString('az-AZ')}</span>
                  </div>
                  {selectedConnection.operator_notes && (
                    <div className="mb-2">
                      <Label className="text-zinc-500 text-xs">Operator Qeydləri</Label>
                      <p>{selectedConnection.operator_notes}</p>
                    </div>
                  )}
                  {selectedConnection.notes && (
                    <div>
                      <Label className="text-zinc-500 text-xs">Ümumi Qeydlər</Label>
                      <p>{selectedConnection.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Şəkillər Bölməsi */}
              {selectedConnection.completion_images && selectedConnection.completion_images.length > 0 && (
                <div className="border rounded-lg p-4 bg-green-50">
                  <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" /> Texnikin Şəkilləri ({selectedConnection.completion_images.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedConnection.completion_images.map((img, idx) => (
                      <img
                        key={idx}
                        src={getImageUrl(img)}
                        alt={`Şəkil ${idx + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openImageInNewTab(img, selectedConnection.id, idx)}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-green-600 mt-2">Böyütmək üçün şəklin üzərinə klikləyin (yeni tab-da açılacaq)</p>
                </div>
              )}

              {/* Şərhlər Bölməsi */}
              <div className="border rounded-lg p-4 bg-indigo-50">
                <h3 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Şərhlər ({selectedConnection.comments?.length || 0})
                </h3>
                
                {/* Mövcud şərhlər */}
                <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                  {selectedConnection.comments && selectedConnection.comments.length > 0 ? (
                    selectedConnection.comments.map((comment, idx) => (
                      <div key={comment.id || idx} className="bg-white p-3 rounded-lg border">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-sm text-indigo-700">
                            {comment.full_name || comment.username}
                          </span>
                          <span className="text-xs text-zinc-400">
                            {new Date(comment.created_at).toLocaleString('az-AZ')}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-zinc-700">{comment.text}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-zinc-500 text-center py-4">Hələ şərh yoxdur</p>
                  )}
                </div>
                
                {/* Yeni şərh əlavə et */}
                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Şərh yazın..."
                    className="flex-1 bg-white"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <Button
                    size="sm"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || sendingComment}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Tarixlər */}
              <div className="border-t pt-4 text-xs text-zinc-500">
                <div className="flex justify-between">
                  <span>Yaradılıb: {new Date(selectedConnection.created_at).toLocaleString('az-AZ')}</span>
                  {selectedConnection.updated_at && (
                    <span>Son yenilənmə: {new Date(selectedConnection.updated_at).toLocaleString('az-AZ')}</span>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Reject Review Dialog (for operator to mark as not_completed) */}
      {showRejectReviewDialog && selectedConnection && (
        <Dialog open={showRejectReviewDialog} onOpenChange={closeRejectReviewDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Tamamlanmadı</DialogTitle>
              <DialogDescription>Bu qoşulmanı Tamamlanmadı olaraq qeyd edin</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="font-medium text-amber-800">Texnik imtina səbəbi:</p>
                <p className="text-amber-700">{REJECTION_REASONS.find(r => r.value === selectedConnection.rejection_reason)?.label || selectedConnection.rejection_reason}</p>
                {selectedConnection.rejection_note && (
                  <p className="text-amber-600 mt-1 text-sm">{selectedConnection.rejection_note}</p>
                )}
              </div>
              <div>
                <Label>Operator Qeydi *</Label>
                <Textarea 
                  value={operatorNote}
                  onChange={(e) => setOperatorNote(e.target.value)}
                  placeholder="Müştəri ilə danışıqdan sonrakı qeyd..."
                  rows={3}
                  required
                />
              </div>
              <Button 
                className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                onClick={async () => {
                  if (!operatorNote.trim()) {
                    toast.error('Qeyd daxil edin');
                    return;
                  }
                  try {
                    await axios.put(`${API}/connections/${selectedConnection.id}/status/not_completed`, 
                      { operator_note: operatorNote },
                      { headers }
                    );
                    toast.success('Qoşulma "Tamamlanmadı" olaraq qeyd edildi');
                    setShowRejectReviewDialog(false);
                    setOperatorNote('');
                    fetchData();
                  } catch (error) {
                    toast.error('Xəta baş verdi');
                  }
                }}
              >
                Qoşulmadı Olaraq Qeyd Et
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Connection Dialog */}
      {showEditDialog && editConnection && (
        <Dialog open={showEditDialog} onOpenChange={closeEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Qoşulmanı Dəyiş</DialogTitle>
              <DialogDescription>{editConnection.connection_number}</DialogDescription>
            </DialogHeader>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await axios.put(`${API}/connections/${editConnection.id}`, editConnection, { headers });
                toast.success('Qoşulma yeniləndi');
                setShowEditDialog(false);
                setEditConnection(null);
                fetchData();
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Xəta baş verdi');
              }
            }} className="space-y-4">
              {/* Customer Info */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Müştəri Məlumatları</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Soyad, Ad, Ata adı *</Label>
                    <Input value={editConnection.customer_name || ''} onChange={(e) => setEditConnection({...editConnection, customer_name: e.target.value})} required className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Telefon *</Label>
                    <Input value={editConnection.customer_phone || ''} onChange={(e) => setEditConnection({...editConnection, customer_phone: e.target.value})} required className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Əlavə telefon</Label>
                    <Input value={editConnection.customer_phone_2 || ''} onChange={(e) => setEditConnection({...editConnection, customer_phone_2: e.target.value})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={editConnection.customer_email || ''} onChange={(e) => setEditConnection({...editConnection, customer_email: e.target.value})} className="border-gray-300" />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="border rounded-lg p-4 space-y-3">
                <h3 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4" /> Qoşulma Ünvanı</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Şəhər/Rayon</Label>
                    <Input value={editConnection.city || ''} onChange={(e) => setEditConnection({...editConnection, city: e.target.value})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Qəsəbə/Kənd</Label>
                    <Input value={editConnection.district || ''} onChange={(e) => setEditConnection({...editConnection, district: e.target.value})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Küçə</Label>
                    <Input value={editConnection.street || ''} onChange={(e) => setEditConnection({...editConnection, street: e.target.value})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Bina/Ev</Label>
                    <Input value={editConnection.building || ''} onChange={(e) => setEditConnection({...editConnection, building: e.target.value})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Mənzil</Label>
                    <Input value={editConnection.apartment || ''} onChange={(e) => setEditConnection({...editConnection, apartment: e.target.value})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Mərtəbə</Label>
                    <Input value={editConnection.floor || ''} onChange={(e) => setEditConnection({...editConnection, floor: e.target.value})} className="border-gray-300" />
                  </div>
                </div>
                
                {/* Google Maps URL or Coordinates */}
                <div className="pt-2 border-t">
                  <Label>Google Maps URL və ya Koordinatlar</Label>
                  <Input
                    placeholder="https://maps.google.com/?q=40.4093,49.8671 və ya 40.4093, 49.8671"
                    className="mt-1.5 placeholder:text-gray-400 border-gray-300"
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      let lat = null, lng = null;
                      
                      const qMatch = value.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                      if (qMatch) { lat = parseFloat(qMatch[1]); lng = parseFloat(qMatch[2]); }
                      
                      const atMatch = value.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                      if (!lat && atMatch) { lat = parseFloat(atMatch[1]); lng = parseFloat(atMatch[2]); }
                      
                      const coordMatch = value.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/);
                      if (!lat && coordMatch) { lat = parseFloat(coordMatch[1]); lng = parseFloat(coordMatch[2]); }
                      
                      const llMatch = value.match(/ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                      if (!lat && llMatch) { lat = parseFloat(llMatch[1]); lng = parseFloat(llMatch[2]); }
                      
                      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                        setEditConnection({...editConnection, latitude: lat, longitude: lng});
                      }
                    }}
                  />
                  <p className="text-xs text-zinc-500 mt-1">Google Maps linkini yapışdırın, koordinatlar avtomatik çıxarılacaq</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Enlik (Latitude)</Label>
                    <Input type="number" step="any" value={editConnection.latitude || ''} onChange={(e) => setEditConnection({...editConnection, latitude: parseFloat(e.target.value)})} className="border-gray-300" />
                  </div>
                  <div>
                    <Label>Uzunluq (Longitude)</Label>
                    <Input type="number" step="any" value={editConnection.longitude || ''} onChange={(e) => setEditConnection({...editConnection, longitude: parseFloat(e.target.value)})} className="border-gray-300" />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Qeydlər</Label>
                <Textarea value={editConnection.operator_notes || ''} onChange={(e) => setEditConnection({...editConnection, operator_notes: e.target.value})} rows={2} className="border-gray-300" />
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white">
                Yadda Saxla
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Image Viewer */}
      {showImageViewer && (
        <ImageViewer
          images={imageViewerImages}
          initialIndex={imageViewerIndex}
          onClose={() => setShowImageViewer(false)}
        />
      )}
    </div>
  );
}
